/**
 * NdfSigningDetails
 *
 * Read-only signing details for the NDF signing experience:
 * - "Signing as" identity card
 * - Multi-signer context
 * - Signature progress (header + animated bar)
 * - Per-field status list (signed / pending)
 * - Expiration notice
 *
 * Rendered in BOTH the desktop sidebar and the mobile slide-up drawer
 * (NdfMobileSigningDrawer) so the two surfaces stay in sync.
 *
 * Signing itself happens INLINE in the document (NdfRenderer "Click to sign"
 * buttons), so this panel is purely informational.
 */
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronRight, Users, Clock } from 'lucide-react';

const progressVariants = {
  initial: { width: 0 },
  animate: (progress: number) => ({
    width: `${progress}%`,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  }),
};

export interface NdfSigningDetailsField {
  id: string;
  label: string;
  isSigned: boolean;
}

export interface NdfSigningDetailsProps {
  signerName: string;
  email?: string;
  signerContext?: { role: string; order: number; totalSigners: number };
  signedCount: number;
  requiredCount: number;
  progressPercent: number;
  allRequiredSigned: boolean;
  fields: NdfSigningDetailsField[];
  expiresAt?: string;
}

export function NdfSigningDetails({
  signerName,
  email,
  signerContext,
  signedCount,
  requiredCount,
  progressPercent,
  allRequiredSigned,
  fields,
  expiresAt,
}: NdfSigningDetailsProps) {
  return (
    <div className="space-y-4">
      {/* Signer Info */}
      <div className="rounded-lg bg-stone-50 p-3">
        <p className="text-xs font-medium uppercase tracking-wider text-stone-400">
          Signing as
        </p>
        <p className="mt-1 font-medium text-stone-900">{signerName}</p>
        {email && <p className="text-sm text-stone-500">{email}</p>}
      </div>

      {/* Multi-signer Context */}
      {signerContext && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-start gap-2">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-800">
                {signerContext.role}
              </p>
              <p className="text-xs text-blue-600">
                Signer {signerContext.order} of {signerContext.totalSigners}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-stone-700">
            Signature Progress
          </p>
          <span className="text-xs font-medium text-stone-500">
            {signedCount} of {requiredCount}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="relative h-2 overflow-hidden rounded-full bg-stone-100">
          <motion.div
            className={`absolute inset-y-0 left-0 rounded-full ${
              allRequiredSigned ? 'bg-emerald-500' : 'bg-primary-500'
            }`}
            variants={progressVariants}
            initial="initial"
            animate="animate"
            custom={progressPercent}
          />
        </div>

        {/* Signature Fields List */}
        {fields.length > 0 && (
          <div className="space-y-2">
            {fields.map((field) => (
              <div
                key={field.id}
                className={`flex items-center justify-between rounded-lg border p-3 transition-colors ${
                  field.isSigned
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-stone-200 bg-white'
                }`}
              >
                <span
                  className={`text-sm ${
                    field.isSigned ? 'text-emerald-700' : 'text-stone-600'
                  }`}
                >
                  {field.label}
                </span>
                {field.isSigned ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-stone-400" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Prompt when no signatures yet */}
        {signedCount === 0 && fields.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-blue-200 bg-blue-50 p-3"
          >
            <p className="text-sm text-blue-800">
              Scroll through the document and tap the signature fields to add
              your signature.
            </p>
          </motion.div>
        )}
      </div>

      {/* Expiration Warning */}
      {expiresAt && (
        <div className="flex items-center gap-2 text-xs text-stone-400">
          <Clock className="h-3.5 w-3.5" />
          <span>Expires {new Date(expiresAt).toLocaleDateString()}</span>
        </div>
      )}
    </div>
  );
}

export default NdfSigningDetails;
